import { supabase } from './supabase';

// PWA Session Management
export class PWASessionManager {
  private static readonly STORAGE_KEYS = {
    REMEMBER_SESSION: 'nosh-remember-session',
    SESSION_TIMESTAMP: 'nosh-session-timestamp',
    IS_PWA: 'nosh-is-pwa',
    LAST_ACTIVITY: 'nosh-last-activity'
  };

  // Check if user is using PWA (added to home screen)
  static isPWA(): boolean {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true || // iOS Safari PWA
      localStorage.getItem(this.STORAGE_KEYS.IS_PWA) === 'true'
    );
  }

  // Enable long-term session persistence
  static enablePersistence(rememberMe: boolean = false): void {
    const isPWA = this.isPWA();
    
    // For PWA users, always enable persistence regardless of remember me checkbox
    // For regular browsers, respect the remember me preference
    const shouldPersist = isPWA || rememberMe;
    
    if (shouldPersist) {
      localStorage.setItem(this.STORAGE_KEYS.REMEMBER_SESSION, 'true');
      localStorage.setItem(this.STORAGE_KEYS.SESSION_TIMESTAMP, Date.now().toString());
      localStorage.setItem(this.STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
      
      if (isPWA) {
        localStorage.setItem(this.STORAGE_KEYS.IS_PWA, 'true');
        console.log('📱 PWA session persistence enabled');
      }
      
      console.log('🔐 Long-term session persistence enabled');
    }
  }

  // Update last activity timestamp
  static updateActivity(): void {
    if (this.shouldPersistSession()) {
      localStorage.setItem(this.STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
    }
  }

  // Check if session should persist
  static shouldPersistSession(): boolean {
    return (
      localStorage.getItem(this.STORAGE_KEYS.REMEMBER_SESSION) === 'true' ||
      this.isPWA()
    );
  }

  // Initialize session management
  static async initializeSession(): Promise<void> {
    if (this.shouldPersistSession()) {
      try {
        // Attempt to restore session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.warn('Session restoration failed:', error);
          this.clearSessionData();
          return;
        }

        if (session) {
          console.log('✅ Session restored successfully');
          this.updateActivity();
          
          // Auto-refresh token if needed
          if (session.expires_at && session.expires_at * 1000 < Date.now() + 300000) { // 5 minutes before expiry
            console.log('🔄 Refreshing token proactively');
            await supabase.auth.refreshSession();
          }
        } else {
          console.log('ℹ️ No existing session found');
        }
      } catch (error) {
        console.error('Session initialization error:', error);
      }
    }
  }

  // Setup automatic token refresh for long-term sessions
  static setupAutoRefresh(): void {
    if (this.shouldPersistSession()) {
      // Refresh token every 45 minutes (tokens expire in 1 hour by default)
      setInterval(async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            console.log('🔄 Auto-refreshing session token');
            await supabase.auth.refreshSession();
            this.updateActivity();
          }
        } catch (error) {
          console.error('Auto-refresh failed:', error);
        }
      }, 45 * 60 * 1000); // 45 minutes
    }
  }

  // Clear session data
  static clearSessionData(): void {
    Object.values(this.STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    console.log('🧹 Session data cleared');
  }

  // Handle app visibility changes (when user switches apps)
  static setupVisibilityHandlers(): void {
    // Removed automatic session checks on visibility/focus changes
    // Sessions are already managed by Supabase auth state changes
    // No need to re-initialize on tab switching since:
    // 1. User data doesn't change when switching tabs
    // 2. Session tokens are managed automatically by Supabase
    // 3. Causes unnecessary database calls and loading states
    
    // Only update activity timestamp when app becomes visible
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.shouldPersistSession()) {
        this.updateActivity(); // Just update activity, don't re-initialize
      }
    });

    window.addEventListener('focus', () => {
      if (this.shouldPersistSession()) {
        this.updateActivity(); // Just update activity, don't re-initialize
      }
    });
  }

  // Get session info for debugging
  static getSessionInfo(): object {
    return {
      isPWA: this.isPWA(),
      shouldPersist: this.shouldPersistSession(),
      rememberSession: localStorage.getItem(this.STORAGE_KEYS.REMEMBER_SESSION),
      sessionTimestamp: localStorage.getItem(this.STORAGE_KEYS.SESSION_TIMESTAMP),
      lastActivity: localStorage.getItem(this.STORAGE_KEYS.LAST_ACTIVITY),
      timeSinceLastActivity: Date.now() - parseInt(localStorage.getItem(this.STORAGE_KEYS.LAST_ACTIVITY) || '0')
    };
  }
}

// Export convenience functions
export const initializePWASession = () => PWASessionManager.initializeSession();
export const enablePWAPersistence = (rememberMe: boolean) => PWASessionManager.enablePersistence(rememberMe);
export const isPWAMode = () => PWASessionManager.isPWA();
export const setupPWAHandlers = () => {
  PWASessionManager.setupAutoRefresh();
  PWASessionManager.setupVisibilityHandlers();
}; 